import { Box, ButtonBase, Collapse, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import { SITE_STEPS, marketingPalette } from "@/components/marketing/tokens";
import { OVER_BAR } from "@/components/marketing/header/tokens";
import type { Heading } from "@/content/types";
import ArticleContents from "./ArticleContents";

/**
 * The contents below the site's wide step, as the published page draws them: a
 * fixed bar on the bottom edge holding a "Table of Contents" trigger with the
 * list growing out of it. The list is the desktop rail's own, in its `sheet`
 * variant, so the two cannot drift.
 *
 * Every value measured at 767: the bar sits 63.19px tall closed, the trigger is
 * a 40px row on the wash, and the panel opens to a 256px scroller.
 */
const TRIGGER_LABEL = "Table of Contents";
const BAR_RADIUS = "16px 16px 0 0";
const BAR_BORDER = `2px solid ${alpha(marketingPalette.ink, 0.2)}`;
const BAR_SHADOW = "0 -2px 19px -4px rgba(21, 11, 63, 0.2)";
/** The rail's own easing; the panel is the one thing the published bar snaps. */
const OPEN_MS = 200;
const OPEN_EASING = "cubic-bezier(0.215, 0.61, 0.355, 1)";

/** The published trigger's glyph: three rules in a 40px box. */
function ContentsIcon() {
  return (
    <Box
      component="svg"
      viewBox="0 0 40 40"
      fill="none"
      sx={{ width: 20, height: 20, flexShrink: 0 }}
    >
      {[10, 20, 30].map((y) => (
        <path
          key={y}
          d={`M15 ${y}H33.3333`}
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      <path
        d="M6.667 10h.017M6.667 20h.017M6.667 30h.017"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

export default function MobileContents({ headings }: { headings: Heading[] }) {
  const [open, setOpen] = useState(false);
  // The published panel snaps and eases only its margin. This eases the height
  // instead, a deliberate improvement, and the same 200ms curve the rail's own
  // sub-entries use, so the two disclosures behave alike.
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  if (headings.length === 0) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        inset: "auto 0 0",
        zIndex: OVER_BAR,
        display: "flex",
        flexDirection: "column",
        rowGap: 2,
        px: "11.2px",
        pb: "11.2px",
        border: BAR_BORDER,
        borderRadius: BAR_RADIUS,
        backgroundColor: theme.palette.background.paper,
        boxShadow: BAR_SHADOW,
        [theme.breakpoints.up(SITE_STEPS.wide)]: { display: "none" },
      })}
    >
      <Collapse
        in={open}
        timeout={reducedMotion ? 0 : OPEN_MS}
        easing={OPEN_EASING}
      >
        <Box sx={{ pt: 3 }}>
          <ArticleContents headings={headings} variant="sheet" />
        </Box>
      </Collapse>

      <ButtonBase
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        sx={{
          display: "flex",
          // `ButtonBase` leaves the UA's own button font in place.
          fontFamily: "inherit",
          alignItems: "center",
          justifyContent: "center",
          columnGap: 2,
          px: 5,
          py: 3,
          height: 40,
          borderRadius: "16px",
          fontSize: 16,
          lineHeight: "24px",
          color: marketingPalette.inkSubtle,
          backgroundColor: marketingPalette.wash,
        }}
      >
        <ContentsIcon />
        {TRIGGER_LABEL}
      </ButtonBase>
    </Box>
  );
}
